// The scene clock runs only while every due medium can play.
// holder: { id, kind, ready(), settled?() (reopen test), blockedBy?() ("context":
//   data there, audio context down), failed?() (load error), needsData?() (video:
//   false while it only moves within data it already holds), comfort? (video: false
//   skips the comfort wait), startSec? }

export const STALL_DEBOUNCE_MS = 250;
export const VIDEO_COMFORT_MS = 1000;
export const AUDIO_WAIT_LIMIT_MS = 8000;
export const READY_POLL_MS = 200;
// A chosen wait before falling back to the play control, not proof of refusal.
export const CONTEXT_BLOCK_MS = 1500;

const realTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id),
  now: () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
};

export class MediaGate {
  // audioWaitLimitMs: Infinity waits as long as it takes, then goes on.
  constructor({
    listHolders,
    onClose,
    onOpen,
    onTimeout,
    onContextBlocked,
    onFailed,
    isRunning,
    timers,
    videoComfortMs = VIDEO_COMFORT_MS,
    audioWaitLimitMs = AUDIO_WAIT_LIMIT_MS,
  } = {}) {
    this._list = listHolders || (() => []);
    this._onClose = onClose || (() => {});
    this._onOpen = onOpen || (() => {});
    this._onTimeout = onTimeout || (() => {});
    this._onContextBlocked = onContextBlocked || (() => {});
    this._onFailed = onFailed || (() => {});
    this._videoComfortMs = videoComfortMs;
    this._audioWaitLimitMs = audioWaitLimitMs;
    this._failedSeen = new Set();
    this._seenDue = new Set();
    this._isRunning = isRunning || (() => false);
    this._t = { ...realTimers, ...(timers || {}) };
    this._skipped = new Set();
    this._reported = new Set();
    this._reset();
    this._watch = this._t.setInterval(() => this._tick(), READY_POLL_MS);
  }

  _reset() {
    this._closed = false;
    this._held = false;
    this._timedOut = false;
    this._timedOutIds = [];
    this._waitedVideo = false;
    // Waited videos: id → settled since (null while not).
    this._videoReady = new Map();
    this._audioWaited = 0;
    this._cancel("_debounce");
    this._cancel("_comfort");
    this._cancel("_audioTimer");
    this._cancel("_ctxTimer");
    this._ctxAsked = false;
    this._audioSince = null;
  }

  _cancel(name) {
    if (this[name] != null) this._t.clearTimeout(this[name]);
    this[name] = null;
  }

  get closed() {
    return this._closed;
  }

  get timedOut() {
    return this._timedOut;
  }

  isSkipped(id) {
    return this._skipped.has(id);
  }

  _holders() {
    const out = [];
    const failed = [];
    (this._list() || []).forEach((h) => {
      if (!h || this._skipped.has(h.id)) return;
      if (typeof h.failed === "function" && h.failed()) {
        if (!this._failedSeen.has(h.id)) failed.push(h.id);
        return;
      }
      out.push(h);
    });
    if (failed.length) {
      failed.forEach((id) => this._failedSeen.add(id));
      this._onFailed(failed);
    }
    return out;
  }

  _notReady(settled) {
    return this._holders().filter((h) => {
      if (this._reported.has(h.id)) return true;
      const ok = settled && h.settled ? h.settled() : h.ready();
      return !ok;
    });
  }

  // Asked on every start; no stall debounce. Starting a closed gate is a retry.
  check() {
    if (this._closed) {
      this._held = false;
      this._ctxAsked = false;
      this._timedOut = false;
      this._timedOutIds = [];
      this._audioWaited = 0;
      const wait = this._notReady(true);
      this._trackVideoReady(wait);
      if (!wait.length) {
        this._reset();
        this._reported.clear();
        this._markDue();
        return true;
      }
      this._waiting(wait);
      return false;
    }
    this._cancel("_debounce");
    const wait = this._notReady(false);
    if (!wait.length) {
      this._markDue();
      return true;
    }
    this._close(wait);
    return false;
  }

  // Each frame before media are driven: a not-ready newcomer stops the scene at
  // once; returned so the host can hold its clock on that boundary.
  enter() {
    if (this._closed || !this._isRunning()) return [];
    const due = this._holders();
    const fresh = due.filter((h) => !this._seenDue.has(h.id));
    this._seenDue = new Set(due.map((h) => h.id));
    if (!fresh.length) return [];
    const wait = fresh.filter((h) => this._reported.has(h.id) || !h.ready());
    if (wait.length) this._close(wait);
    return wait;
  }

  // enter() without side effects (the host may have an earlier boundary).
  pendingEntries() {
    if (this._closed || !this._isRunning()) return [];
    return this._holders().filter(
      (h) => !this._seenDue.has(h.id) && (this._reported.has(h.id) || !h.ready())
    );
  }

  _markDue() {
    this._seenDue = new Set(this._holders().map((h) => h.id));
  }

  report(id, stalled) {
    if (stalled) {
      this._reported.add(id);
      if (this._closed) {
        this._cancel("_comfort");
        this._evaluate();
      } else {
        this._armStall();
      }
      return;
    }
    this._reported.delete(id);
    if (this._closed) this._evaluate();
    else if (!this._notReady(false).length) this._cancel("_debounce");
  }

  poke() {
    if (this._closed) this._evaluate();
  }

  // Learner pause: no automatic reopen.
  hold() {
    this._cancel("_debounce");
    if (!this._closed) return;
    this._held = true;
    this._cancel("_comfort");
    this._cancel("_ctxTimer");
    this._pauseAudioWait();
  }

  // Seek / scene change. Skipped audio stays skipped for this pass.
  clear() {
    this._reset();
    this._reported.clear();
    this._failedSeen.clear();
    this._seenDue = new Set();
  }

  retryFailed() {
    this._failedSeen.clear();
    this.poke();
  }

  resetPass() {
    this.clear();
    this._skipped.clear();
  }

  retry() {
    if (!this._closed) return;
    this._timedOut = false;
    this._timedOutIds = [];
    this._held = false;
    this._audioWaited = 0;
    this._evaluate();
  }

  // Skips the timed-out audio, also if it got ready meanwhile.
  skip() {
    if (!this._closed) return [];
    const waiting = this._notReady(true)
      .filter(waitsForData)
      .map((h) => h.id);
    const ids = [...new Set([...this._timedOutIds, ...waiting])];
    ids.forEach((id) => this._skipped.add(id));
    this._timedOut = false;
    this._held = false;
    this._audioWaited = 0;
    this._evaluate();
    return ids;
  }

  dispose() {
    this._reset();
    if (this._watch != null) this._t.clearInterval(this._watch);
    this._watch = null;
  }

  _close(wait) {
    this._closed = true;
    this._held = false;
    this._timedOut = false;
    this._waitedVideo = false;
    this._videoReady = new Map();
    this._audioWaited = 0;
    this._waiting(wait);
    this._onClose();
  }

  _waiting(wait) {
    // Comfort follows a video that waited for data (not a buffered seek, not comfort: false).
    wait.forEach((h) => {
      if (h.kind === "video" && h.comfort !== false && (!h.needsData || h.needsData())) {
        this._waitedVideo = true;
        this._videoReady.set(h.id, null);
      }
    });
    this._cancel("_comfort");
    if (wait.some(waitsForData)) this._runAudioWait();
    else this._pauseAudioWait();
    if (wait.some(waitsForContext)) this._runContextWait();
    else this._cancel("_ctxTimer");
  }

  _runContextWait() {
    if (this._ctxTimer != null || this._ctxAsked || this._held || this._timedOut) return;
    this._ctxTimer = this._t.setTimeout(() => {
      this._ctxTimer = null;
      if (!this._closed || this._held || this._timedOut) return;
      if (!this._notReady(true).some(waitsForContext)) return;
      this._ctxAsked = true;
      this._onContextBlocked();
    }, CONTEXT_BLOCK_MS);
  }

  // Comfort counts from each video's own readiness; running dry restarts it.
  _trackVideoReady(wait) {
    const now = this._t.now();
    const waiting = new Set(wait.map((h) => h.id));
    this._videoReady.forEach((since, id) => {
      if (waiting.has(id)) this._videoReady.set(id, null);
      else if (since == null) this._videoReady.set(id, now);
    });
  }

  _comfortLeft() {
    if (!this._waitedVideo || !(this._videoComfortMs > 0)) return 0;
    const now = this._t.now();
    let left = 0;
    this._videoReady.forEach((since) => {
      const rest = since == null ? this._videoComfortMs : this._videoComfortMs - (now - since);
      left = Math.max(left, rest);
    });
    return left;
  }

  _evaluate() {
    if (!this._closed) return;
    const wait = this._notReady(true);
    // Tracked even while held; only opening is gated.
    this._trackVideoReady(wait);
    if (this._held || this._timedOut) return;
    if (wait.length) {
      this._waiting(wait);
      return;
    }
    this._pauseAudioWait();
    const left = this._comfortLeft();
    if (left <= 0) {
      this._open();
      return;
    }
    if (this._comfort != null) return;
    this._comfort = this._t.setTimeout(() => {
      this._comfort = null;
      this._evaluate();
    }, left);
  }

  _open() {
    this._reset();
    this._reported.clear();
    this._markDue();
    this._onOpen();
  }

  _runAudioWait() {
    if (this._audioTimer != null || this._held || this._timedOut) return;
    if (!Number.isFinite(this._audioWaitLimitMs)) return;
    this._audioSince = this._t.now();
    const left = Math.max(0, this._audioWaitLimitMs - this._audioWaited);
    this._audioTimer = this._t.setTimeout(() => {
      this._audioTimer = null;
      this._audioSince = null;
      if (!this._closed || this._held) return;
      const audio = this._notReady(true).filter(waitsForData);
      if (!audio.length) {
        this._evaluate();
        return;
      }
      this._timedOut = true;
      this._timedOutIds = audio.map((h) => h.id);
      this._cancel("_comfort");
      this._onTimeout(this._timedOutIds);
    }, left);
  }

  _pauseAudioWait() {
    if (this._audioTimer == null) return;
    this._cancel("_audioTimer");
    if (this._audioSince != null) this._audioWaited += this._t.now() - this._audioSince;
    this._audioSince = null;
  }

  // Independent of frames: re-checks a closed gate, catches stalls while running.
  _tick() {
    if (this._closed) {
      this._evaluate();
      return;
    }
    if (this._isRunning() && this._notReady(false).length) this._armStall();
  }

  // A seek's brief `waiting` must not stop the scene.
  _armStall() {
    if (this._debounce != null) return;
    this._debounce = this._t.setTimeout(() => {
      this._debounce = null;
      if (this._closed || !this._isRunning()) return;
      const wait = this._notReady(false);
      if (wait.length) this._close(wait);
    }, STALL_DEBOUNCE_MS);
  }
}

const waitsForContext = (h) =>
  h.kind === "audio" && typeof h.blockedBy === "function" && h.blockedBy() === "context";
const waitsForData = (h) => h.kind === "audio" && !waitsForContext(h);

// Media outside the host (AudioCue) register here.
const registered = new Map();
let activeGate = null;

export function registerMediaHolder(holder) {
  registered.set(holder.id, holder);
  return () => {
    if (registered.get(holder.id) === holder) registered.delete(holder.id);
  };
}

// `sec`: the time being decided, so a window starting on this frame counts.
export function registeredMediaHolders(sceneId, sec) {
  return [...registered.values()].filter((h) => h.sceneId() === sceneId && h.due(sec));
}

export function setActiveMediaGate(gate) {
  activeGate = gate;
}

export function pokeMediaGate() {
  if (activeGate) activeGate.poke();
}

export function isMediaSkipped(id) {
  return !!activeGate && activeGate.isSkipped(id);
}
